import _ from 'underscore';

import {
  DEFAULT_NAME_MATCHED_OBJECT_TYPES,
  ORDERED_OBJECT_TYPES_BY_MIGRATION_STEP,
} from 'config-migration/import/ObjectSectionConfig';
import convertImportIdentifiersToPlans from 'config-migration/import/planning-logic/convertImportIdentifiersToPlans';
import {
  MigrationObjectPlanById,
  PrimaryObjectPlan,
  BaseMigrationObjectPlan,
  DestinationObject,
  ImportObject,
  SecondaryObjectPlan,
  ChildPlansAndSubtractedObjects,
} from 'config-migration/types/MigrationObjects';
import {PrimaryObjectType, SecondaryObjectType} from 'config-migration/types/ObjectType';

import {MigrationStep} from '../../types/MigrationStep';
import {ObjectAction, ObjectActionType} from '../../types/objectActions';
import {isTemplatePlannedOnStep} from './templateFiltering';

/**
 * Convert all objects into their initial migration plans - including what default action they have and what
 * other possible actions there are.
 *
 * @param importObjects All import objects from the source (that need to be planned)
 * @param destinationObjects All destination objects (that can potentially be selected to be mapped to an
 *  import object)
 * @returns An array of Objects and their default planning state (MigrationObjectPlan)
 */
export function getInitialMigrationPlans(
  importObjects: Array<ImportObject>,
  destinationObjects: Array<DestinationObject>
): Array<PrimaryObjectPlan> {
  const objectTypeToImportObjects = _.groupBy(importObjects, 'objectType');

  // For each migration step, get initial plans for all object types in that migration step.
  return Object.entries(ORDERED_OBJECT_TYPES_BY_MIGRATION_STEP).flatMap(
    ([migrationStep, objectTypesForStep]): Array<PrimaryObjectPlan> =>
      objectTypesForStep.flatMap((objectType) => {
        const importObjectsOfType = objectTypeToImportObjects[objectType] || [];
        const {baseMigrationObjectPlans, destinationObjectsOfType} = getBaseMigrationObjectPlans(
          importObjectsOfType,
          destinationObjects,
          objectType,
          migrationStep as MigrationStep
        );
        // Convert all BaseMigrationObjectPlans into MigrationObjectPlans using information on
        // their related ImportObject and any previous or default selected actions.
        return baseMigrationObjectPlans.map((plan) => {
          const selectedAction: ObjectAction = plan.selectedAction;
          const importObject = importObjects.find((obj) => obj.identifier.provenanceUri === plan.objectId)!;
          return {
            ...plan,
            selectedAction,
            objectType: importObject.objectType as PrimaryObjectType,
            objectAttributes: importObject.objectAttributes,
            childPlansByType: getChildPlansByType(
              selectedAction,
              importObject.childObjects,
              destinationObjectsOfType
            ),
          };
        });
      })
  );
}

function getChildPlansByType(
  selectedAction: ObjectAction,
  importChildObjects: Array<ImportObject>,
  destinationObjectsOfType: Array<DestinationObject>,
  provenanceUriToPreviouslySelectedAction: Map<string, ObjectAction> = new Map()
): Map<SecondaryObjectType, ChildPlansAndSubtractedObjects> {
  const childMigrationPlans = getChildMigrationPlans(
    selectedAction,
    importChildObjects,
    destinationObjectsOfType,
    provenanceUriToPreviouslySelectedAction
  );
  const subtractedChildObjects = getSubtractedChildObjects(
    selectedAction,
    destinationObjectsOfType,
    childMigrationPlans
  );

  const childMigrationPlansByType = _.groupBy(childMigrationPlans, 'objectType');
  const subtractedChildObjectsByType = _.groupBy(subtractedChildObjects, 'objectType');

  const childPlansByType = new Map();
  const allChildTypes = new Set([
    ...Object.keys(childMigrationPlansByType),
    ...Object.keys(subtractedChildObjectsByType),
  ]);
  for (const childType of allChildTypes) {
    const childMigrationPlans = childMigrationPlansByType[childType] || [];
    const subtractedChildObjects = subtractedChildObjectsByType[childType] || [];
    childPlansByType.set(childType, {childMigrationPlans, subtractedChildObjects});
  }
  return childPlansByType;
}

interface BaseMigrationObjectAndDestinationIdentifiers {
  baseMigrationObjectPlans: Array<BaseMigrationObjectPlan>;
  destinationObjectsOfType: Array<DestinationObject>;
}

/**
 * Converts importObjects and destinationObjects of the current objectType into
 * migration object plans.
 *
 * If these objects have previously been updated and need to be replanned,
 * uses provenanceUriToPreviouslySelectedAction to do the needed updates.
 *
 * @param importObjectsOfType Import objects with the same type as the passed objectType
 * @param destinationObjects All destination objects
 * @param objectType What type of objects to generate plans for
 * @param currentMigrationStep The current step in the migration
 * @param provenanceUriToPreviouslySelectedAction A mapping from Provenance URI to the previously selected
 *  action. If not passed, the method creates the initial object plans.
 */
function getBaseMigrationObjectPlans(
  importObjectsOfType: Array<ImportObject>,
  destinationObjects: Array<DestinationObject>,
  objectType: string,
  currentMigrationStep: MigrationStep,
  provenanceUriToPreviouslySelectedAction: Map<string, ObjectAction> = new Map()
): BaseMigrationObjectAndDestinationIdentifiers {
  const importIdentifiers = importObjectsOfType
    .filter(
      (obj) => objectType !== PrimaryObjectType.Template || isTemplatePlannedOnStep(obj, currentMigrationStep)
    )
    .map((importObject) => importObject.identifier);
  const destinationObjectsOfType = destinationObjects
    .filter((obj) => obj.objectType === objectType)
    // Templates are shown on both the Workflows and Templates pane depending on their object attributes.
    // We should only include non-templates or applicable templates
    .filter(
      (obj) => objectType !== PrimaryObjectType.Template || isTemplatePlannedOnStep(obj, currentMigrationStep)
    );
  const destinationIdentifiers = destinationObjectsOfType.map(
    (destinationObject) => destinationObject.identifier
  );

  const baseMigrationObjectPlans = convertImportIdentifiersToPlans(
    importIdentifiers,
    destinationIdentifiers,
    DEFAULT_NAME_MATCHED_OBJECT_TYPES.includes(objectType),
    provenanceUriToPreviouslySelectedAction
  );

  return {baseMigrationObjectPlans, destinationObjectsOfType};
}

export function getChildMigrationPlans(
  parentSelectedAction: ObjectAction,
  childImportObjects: Array<ImportObject>,
  destinationObjects: Array<DestinationObject>,
  childProvenanceURIToPreviouslySelectedAction: Map<string, ObjectAction> = new Map()
): Array<SecondaryObjectPlan> {
  if (parentSelectedAction.type !== ObjectActionType.UPDATE || childImportObjects.length === 0) {
    return [];
  }
  const targetDestinationObject = destinationObjects.find(
    (destinationObject) =>
      destinationObject.identifier.apiIdentifier === parentSelectedAction.target.apiIdentifier
  )!;

  const allChildMigrationPlans: Array<SecondaryObjectPlan> = [];
  const childImportObjectsByType = _.groupBy(childImportObjects, 'objectType');
  for (const objectType of Object.keys(childImportObjectsByType)) {
    // Collect import and destination children of the same type
    const importChildIdentifiersOfType = childImportObjectsByType[objectType].map(
      (importObject) => importObject.identifier
    );
    const destinationChildIdentifiersOfType = targetDestinationObject.childObjects
      .filter((destinationChild) => destinationChild.objectType === objectType)
      .map((destinationChild) => destinationChild.identifier);

    // Convert to child migration plans
    const baseChildMigrationPlans = convertImportIdentifiersToPlans(
      importChildIdentifiersOfType,
      destinationChildIdentifiersOfType,
      true,
      childProvenanceURIToPreviouslySelectedAction
    );
    // Append to results
    baseChildMigrationPlans.forEach((plan) => {
      const childImportObject = childImportObjects.find(
        (obj) => obj.identifier.provenanceUri === plan.objectId
      )!;

      allChildMigrationPlans.push({
        ...plan,
        objectType: childImportObject.objectType as SecondaryObjectType,
        childPlansByType: getChildPlansByType(
          plan.selectedAction,
          childImportObject.childObjects,
          targetDestinationObject.childObjects,
          childProvenanceURIToPreviouslySelectedAction
        ),
      });
    });
  }
  return allChildMigrationPlans;
}

/**
 * For a selected update action on a source object, get destination child objects that do not exist on the
 * source and are not already archived. This is used to display archived and removed actions in the import
 * wizard.
 */
export function getSubtractedChildObjects(
  parentSelectedAction: ObjectAction,
  destinationObjects: Array<DestinationObject>,
  childMigrationPlans: Array<BaseMigrationObjectPlan>
): Array<DestinationObject> {
  if (parentSelectedAction.type !== ObjectActionType.UPDATE) {
    return [];
  }
  const targetDestinationObject = destinationObjects.find(
    (destinationObject) =>
      destinationObject.identifier.apiIdentifier === parentSelectedAction.target.apiIdentifier
  )!;

  const foundChildProvenanceUris = new Set();
  childMigrationPlans.forEach((childPlan) => {
    if (childPlan.selectedAction.type === ObjectActionType.UPDATE) {
      foundChildProvenanceUris.add(childPlan.selectedAction.target.provenanceUri);
    }
  });
  return targetDestinationObject.childObjects.filter(
    (destinationChild) =>
      destinationChild.identifier.archived === false &&
      !foundChildProvenanceUris.has(destinationChild.identifier.provenanceUri)
  );
}

/**
 * Every time plans are updated by the user, we need to re-generate plans for all objects of the type(s) that
 * were updated. This is because the list of additional actions are affected by the selected actions of
 * objects of the same type that are planned on the same step. Given a set of updated plans, return the
 * full set of plans to update in the current step.
 */
export function getMigrationPlansToUpdate(
  updatedMigrationPlans: Array<PrimaryObjectPlan>,
  currentMigrationPlans: Array<PrimaryObjectPlan>,
  currentMigrationStep: MigrationStep
): Array<PrimaryObjectPlan> {
  const updatedPlansByType = _.groupBy(updatedMigrationPlans, 'objectType');
  const updatedPlansById = _.indexBy(updatedMigrationPlans, 'objectId');

  return [
    ...currentMigrationPlans.filter(
      (migrationPlan) =>
        updatedPlansByType[migrationPlan.objectType] &&
        !updatedPlansById[migrationPlan.objectId] &&
        (migrationPlan.objectType !== PrimaryObjectType.Template ||
          isTemplatePlannedOnStep(migrationPlan, currentMigrationStep))
    ),
    ...updatedMigrationPlans,
  ];
}

/**
 * Using initial importObjects and destinationObjects, convert all objects into their initial migration plans -
 * including what default action they have and what other actions are available to them.
 *
 * This method assumes that getInitialMigrationPlans has already been run.
 *
 * @param importObjects All import objects from the source (that need to be planned)
 * @param destinationObjects All destination objects (that can potentially be selected to be mapped to an
 *  import object)
 * @param updatedMigrationPlans The list of plans that have changed based on the user's most recent selections.
 * @param currentMigrationPlans The list of the current plans based on the initial plans and the user's
 *  previous selections.
 * @param currentMigrationStep What step within the Import flow we are updating the plans for
 * @returns An array of Objects and their updated planning state (MigrationObjectPlan)
 */
export function updateMigrationPlans(
  importObjects: Array<ImportObject>,
  destinationObjects: Array<DestinationObject>,
  updatedMigrationPlans: Array<PrimaryObjectPlan>,
  currentMigrationPlans: Array<PrimaryObjectPlan>,
  currentMigrationStep: MigrationStep
): Array<PrimaryObjectPlan> {
  const plansToUpdate = getMigrationPlansToUpdate(
    updatedMigrationPlans,
    currentMigrationPlans,
    currentMigrationStep
  );
  const migrationProvenanceURIs = plansToUpdate.map((plan) => plan.objectId);
  const objectTypeToMigrationPlans = _.groupBy(plansToUpdate, 'objectType');

  return Object.entries(objectTypeToMigrationPlans).flatMap(
    ([objectType, migrationPlansOfType]): Array<PrimaryObjectPlan> => {
      const relevantImportObjects = importObjects.filter((obj) =>
        migrationProvenanceURIs.includes(obj.identifier.provenanceUri)
      );

      // TODO: Refactor to take in initialAction so it doesn't need to recomputed
      const provenanceURIToPreviouslySelectedAction = getProvenanceURIToSelectedAction(migrationPlansOfType);
      const {baseMigrationObjectPlans} = getBaseMigrationObjectPlans(
        relevantImportObjects,
        destinationObjects,
        objectType,
        currentMigrationStep,
        provenanceURIToPreviouslySelectedAction
      );

      const identifierToUpdatedMigrationObjectPlan = _.indexBy(baseMigrationObjectPlans, 'objectId');

      // For each of the pre-existing MigrationObjectPlans, using the related ImportObject and any previously
      // selected actions for it and its Child Plans, return an updated MigrationObjectPlan.
      return migrationPlansOfType.map((migrationPlan) => {
        const importObject = relevantImportObjects.find(
          (obj) => obj.identifier.provenanceUri === migrationPlan.objectId
        )!;
        return {
          ...identifierToUpdatedMigrationObjectPlan[migrationPlan.objectId],
          childPlansByType: getChildPlansByType(
            migrationPlan.selectedAction,
            importObject.childObjects,
            destinationObjects,
            provenanceURIToPreviouslySelectedAction
          ),
          objectType: migrationPlan.objectType,
          objectAttributes: migrationPlan.objectAttributes,
        };
      });
    }
  );
}

export function allChildMigrationPlans(
  plan: PrimaryObjectPlan | SecondaryObjectPlan
): Array<SecondaryObjectPlan> {
  return Array.from(plan.childPlansByType.values()).flatMap(
    (childPlansAndSubtracted) => childPlansAndSubtracted.childMigrationPlans
  );
}

/**
 * Get mapping from provenance URI to selected actions of all migration plans and their child plans, inclusive
 * of all nested child levels.
 */
function getProvenanceURIToSelectedAction(
  migrationPlans: Array<PrimaryObjectPlan | SecondaryObjectPlan>
): Map<string, ObjectAction> {
  return migrationPlans.reduce((map, migrationPlan) => {
    map.set(migrationPlan.objectId, migrationPlan.selectedAction);
    for (const [objectId, selectedAction] of getProvenanceURIToSelectedAction(
      allChildMigrationPlans(migrationPlan)
    )) {
      map.set(objectId, selectedAction);
    }
    return map;
  }, new Map<string, ObjectAction>());
}

/**
 * Get objects on the destination that are not matched for update by config migration.
 */
export function getUnmatchedDestinationObjects(
  migrationPlansById: MigrationObjectPlanById,
  destinationObjects: Array<DestinationObject>
): Array<DestinationObject> {
  const selectedApiIdsToUpdate = _.compact(
    Object.values(migrationPlansById).map((plan) =>
      plan.selectedAction.type === ObjectActionType.UPDATE ? plan.selectedAction.target.apiIdentifier : null
    )
  );
  return destinationObjects.filter(
    (destinationObject) => !selectedApiIdsToUpdate.includes(destinationObject.identifier.apiIdentifier)
  );
}
